import React from "react";
import { Link } from "react-router-dom";
import "../assets/landing.css";

const features = [
  {
    icon: "⏱️",
    title: "Beat the Clock",
    description:
      "Every question has a countdown timer. Answer fast to stay sharp and keep the pressure on.",
  },
  {
    icon: "📚",
    title: "Real-World Topics",
    description:
      "Banking, investing, debt, housing, and more — trivia built around practical financial literacy.",
  },
  {
    icon: "🎯",
    title: "Level Up",
    description:
      "Start easy and climb through levels. Each category has multiple difficulty tiers to master.",
  },
  {
    icon: "💡",
    title: "Learn as You Play",
    description:
      "Every answer comes with an explanation so you walk away smarter, not just with a score.",
  },
];

const steps = [
  { number: "01", title: "Pick a category", text: "Choose from banking, income, investments, and more." },
  { number: "02", title: "Select your level", text: "Match the difficulty to your experience — beginner to expert." },
  { number: "03", title: "Answer & learn", text: "Race the timer, pick your answer, and read the insight after each question." },
];

const categories = [
  "Banking Basics",
  "Types of Income",
  "Investment Literacy",
  "Debt Management",
  "Housing & Income",
  "Insurance",
];

const Landing = () => {
  return (
    <div className="landing">
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-logo">
            <span className="landing-logo-icon">?</span>
            <span>Trivia<span className="landing-logo-accent">Quest</span></span>
          </Link>
          <div className="landing-nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <Link to="/play" className="landing-nav-cta">
              Play Now
            </Link>
          </div>
        </div>
      </nav>

      <header className="landing-hero">
        <div className="landing-hero-glow" aria-hidden="true" />
        <div className="landing-hero-content">
          <p className="landing-eyebrow">Online Financial Literacy Trivia</p>
          <h1 className="landing-headline">
            Test your money smarts.
            <br />
            <span className="landing-headline-accent">Have fun doing it.</span>
          </h1>
          <p className="landing-subheadline">
            Challenge yourself with timed trivia on banking, investing, debt, and
            everyday finance — designed for learners aged 14 and up.
          </p>
          <div className="landing-hero-actions">
            <Link to="/play" className="landing-btn-primary">
              Start Playing
              <span aria-hidden="true">→</span>
            </Link>
            <a href="#how-it-works" className="landing-btn-secondary">
              See How It Works
            </a>
          </div>
          <div className="landing-hero-stats">
            <div className="landing-stat">
              <strong>10+</strong>
              <span>Categories</span>
            </div>
            <div className="landing-stat-divider" aria-hidden="true" />
            <div className="landing-stat">
              <strong>4</strong>
              <span>Difficulty Levels</span>
            </div>
            <div className="landing-stat-divider" aria-hidden="true" />
            <div className="landing-stat">
              <strong>∞</strong>
              <span>Replay Value</span>
            </div>
          </div>
        </div>

        <div className="landing-hero-card" aria-hidden="true">
          <div className="landing-preview">
            <div className="landing-preview-header">
              <span className="landing-preview-dot" />
              <span className="landing-preview-dot" />
              <span className="landing-preview-dot" />
            </div>
            <div className="landing-preview-timer">8</div>
            <p className="landing-preview-question">
              Which of the following is an example of earned income?
            </p>
            <ul className="landing-preview-answers">
              <li>Salary from a job</li>
              <li>Birthday money from Grandma</li>
              <li>Winning a dance battle</li>
              <li>Free pizza from a friend</li>
            </ul>
            <div className="landing-preview-progress">
              <div className="landing-preview-progress-bar" />
            </div>
            <p className="landing-preview-meta">Question 3 of 40 · Income Basics</p>
          </div>
        </div>
      </header>

      <section id="features" className="landing-section">
        <div className="landing-section-inner">
          <p className="landing-section-eyebrow">Why Play</p>
          <h2 className="landing-section-title">More than just a quiz</h2>
          <p className="landing-section-desc">
            TriviaQuest turns financial education into a game you actually want to play.
          </p>
          <div className="landing-features-grid">
            {features.map((feature) => (
              <article key={feature.title} className="landing-feature-card">
                <span className="landing-feature-icon">{feature.icon}</span>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="landing-section landing-section-alt">
        <div className="landing-section-inner">
          <p className="landing-section-eyebrow">How It Works</p>
          <h2 className="landing-section-title">Three steps to smarter finances</h2>
          <div className="landing-steps">
            {steps.map((step) => (
              <div key={step.number} className="landing-step">
                <span className="landing-step-number">{step.number}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-section-inner landing-categories-block">
          <div>
            <p className="landing-section-eyebrow">Topics Covered</p>
            <h2 className="landing-section-title">Pick your challenge</h2>
            <p className="landing-section-desc">
              From first paycheck basics to investment concepts — there&apos;s a category for every stage of your financial journey.
            </p>
            <Link to="/play" className="landing-btn-primary landing-btn-inline">
              Browse Categories
              <span aria-hidden="true">→</span>
            </Link>
          </div>
          <ul className="landing-category-list">
            {categories.map((cat) => (
              <li key={cat}>
                <span className="landing-category-bullet" aria-hidden="true" />
                {cat}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="landing-cta-banner">
        <div className="landing-cta-inner">
          <h2>Ready to prove what you know?</h2>
          <p>Jump in free — no sign-up required. Just pick a category and go.</p>
          <Link to="/play" className="landing-btn-primary landing-btn-large">
            Play Trivia Now
            <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <p>TriviaQuest · Financial Literacy Trivia Game</p>
      </footer>
    </div>
  );
};

export default Landing;
